/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';

import {ApiConfig} from '../../components/api/api';
import {AdnHttpService} from '../../components/util/adn-http.service';
import {
  AdnHttpSender,
  createAdnHttpResourceSender
} from '../../components/util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../components/auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from './directives/error/api-error-handler.service';

const ALL_OTHERS_STATS_GROUP_ID = 'all-others-stats-group-id';

function isAllOthersStatsGroupId(url: string): boolean {
  return !!url && String(url).indexOf(ALL_OTHERS_STATS_GROUP_ID) !== -1;
}

@Injectable({providedIn: 'root'})
export class AdnShotterService {
  private readonly send: AdnHttpSender;

  constructor(
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService
  ) {
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  }

  deleteScreenshot(screenshot: any): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'deleteshot']),
      params: _.assign({}, {format: 'json', concise: true, shotId: screenshot.idOfShot})
    });
  }

  fetchAll(): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'fetchall']),
      params: _.assign({}, {format: 'json', concise: true})
    });
  }

  fetchAllPreviews(): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'fetchallpreviews']),
      params: _.assign({}, {format: 'json', concise: true})
    });
  }

  fetchAllTransparency(): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'fetchalltransparency']),
      params: _.assign({}, {format: 'json', concise: true})
    });
  }

  missingPreview(): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'missingpreview']),
      params: _.assign({}, {format: 'json', concise: true})
    });
  }

  missingTransparency(): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'missingtransparency']),
      params: _.assign({}, {format: 'json', concise: true})
    });
  }

  previewsByCreative(page?: number): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'previewsbycreative']),
      params: _.assign({}, {format: 'json', concise: true, page: page || 1})
    });
  }

  transparencyByLineItem(page?: number, lineItemId?: string): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'transparencybylineitem']),
      params: _.pickBy({
        format: 'json',
        concise: true,
        page: page || 1,
        lineItemId: lineItemId || undefined
      }, (value) => value !== undefined && value !== null && value !== '')
    });
  }

  fetchScreenshots(lineItemId: string, creativeId?: string): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'results']),
      params: _.assign({}, {format: 'json', concise: true, lineItemId, creativeId})
    });
  }

  takeShot(
    creativeId: string | null,
    lineItemId: string | null,
    url: string,
    device?: string,
    bypassSave?: boolean,
    headless?: boolean
  ): Promise<any> {
    if (isAllOthersStatsGroupId(url)) {
      return Promise.resolve(null);
    }
    const params: any = {
      format: 'json',
      concise: true,
      url: url.replace('https://', '').replace('http://', ''),
      creativeId,
      lineItemId,
      device: device || 'DESKTOP'
    };
    if (bypassSave !== undefined) {
      params.bypassSave = bypassSave;
    }
    if (headless !== undefined) {
      params.headless = headless;
    }
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'takeshot']),
      params
    });
  }

  takePreview(creativeId: string, device?: string): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'takepreview']),
      params: _.assign({}, {format: 'json', concise: true, creativeId, device: device || 'DESKTOP'})
    });
  }

  takeTransparency(lineItemId: string, device?: string): Promise<any> {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(['adnshotter', 'taketransparency']),
      params: _.assign({}, {format: 'json', concise: true, lineItemId, device: device || 'DESKTOP'})
    });
  }
}
